import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion, revealWords, revealBlock } from "../lib/motion";
import handPhone from "../assets/images/hand-phone.webp";
import blobOrange from "../assets/decor/blob-orange.webp";
import "./Getaway.css";

export default function Getaway() {
  const rootRef = useRef(null);
  const phoneRef = useRef(null);
  const plateRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const ctx = gsap.context(() => {
      revealWords(root.querySelector(".getaway__title"), { trigger: root });
      revealBlock(root.querySelector(".getaway__body"), { delay: 0.15 });

      if (prefersReducedMotion()) {
        gsap.set([plateRef.current, phoneRef.current, ".getaway__blob"], {
          autoAlpha: 1,
          clipPath: "none",
          scale: 1,
          y: 0,
        });
        return;
      }

      // Sand plate unfolds from the left, then the mockup drops into it.
      gsap.fromTo(
        plateRef.current,
        { clipPath: "inset(0% 100% 0% 0%)" },
        {
          clipPath: "inset(0% 0% 0% 0%)",
          duration: 1.25,
          ease: "expo.out",
          scrollTrigger: { trigger: root, start: "top 78%", once: true },
        }
      );

      gsap.fromTo(
        phoneRef.current,
        { autoAlpha: 0, y: 54, scale: 0.94 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 1.35,
          ease: "expo.out",
          delay: 0.2,
          scrollTrigger: { trigger: root, start: "top 78%", once: true },
        }
      );

      gsap.fromTo(
        ".getaway__blob",
        { autoAlpha: 0, scale: 0.3 },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 1,
          ease: "back.out(2)",
          scrollTrigger: { trigger: root, start: "top 78%", once: true },
        }
      );

      // Gentle counter-scroll so the mockup floats inside its plate.
      gsap.to(phoneRef.current, {
        yPercent: -9,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 0.7 },
      });

      gsap.to(".getaway__blob", {
        rotate: 130,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 1.2 },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section className="getaway section" ref={rootRef}>
      <div className="container getaway__grid">
        <figure className="getaway__media">
          <span className="getaway__plate" ref={plateRef} aria-hidden="true" />
          <img
            ref={phoneRef}
            className="getaway__phone"
            src={handPhone}
            alt="A hand holding a phone showing the Private Stays booking screen for a private farm in Jordan."
            width="800"
            height="665"
            loading="lazy"
            decoding="async"
          />
        </figure>

        <div className="getaway__copy">
          <img className="getaway__blob" src={blobOrange} alt="" aria-hidden="true" width="65" height="65" />
          <h2 className="getaway__title section-title">YOUR PERFECT GETAWAY</h2>
          <p className="getaway__body lede">
            PRIVATE STAYS offers a unique opportunity to book stunning farmhouses and camps
            across Jordan. Whether you&rsquo;re seeking a tranquil escape in nature or a
            memorable camping experience with family and friends, you&rsquo;ll find the perfect
            place for your stay.
          </p>
        </div>
      </div>
    </section>
  );
}
